import { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';

import { resolveSessionUserEmail } from '@lib/analytics/claritySessionUser';
import { syncPostHogIdentity } from '@lib/analytics/posthogIdentity';
import { useAuth } from '@lib/auth';
import { isPostHogLoaded, onPostHogLoaded, posthog } from '@lib/posthog.lib';
import type { RootState } from '@lib/redux.lib/store';

/**
 * Resolves the Cognito actor id so impersonation sessions stay attributed to
 * the admin, not the impersonated target profile.
 */
const getAuthenticatedActorId = (
  authUserSession: ReturnType<typeof useAuth>['authUserSession']
): string | undefined => {
  const fromAttributes =
    authUserSession?.attributes?.['custom:castifi_user_id'];
  const fromToken =
    authUserSession?.signInUserSession?.idToken?.payload?.[
      'custom:castifi_user_id'
    ];

  return fromAttributes || fromToken || undefined;
};

/**
 * Enables PostHog pageviews, pageleave, and session recording only after
 * auth bootstrap finishes so anonymous/stale identities are not captured.
 *
 * Also re-syncs identity when the SDK becomes ready after auth: middleware
 * may have skipped identify/reset while `posthog.__loaded` was still false.
 * Identity always uses the Cognito session user (admin during impersonation)
 * and their email — never the impersonated target's.
 */
export const PostHogAnalyticsLifecycle = (): null => {
  const router = useRouter();
  const { sessionLoading, authUserSession } = useAuth();
  const profile = useSelector((state: RootState) => state.app.user.profile);
  const [isPostHogReady, setIsPostHogReady] = useState(isPostHogLoaded);

  // Prefer Cognito session identity. Redux profile may still be the
  // impersonated target after exit while impersonatedUserId is already null.
  const analyticsUserId = useMemo(
    () => getAuthenticatedActorId(authUserSession) ?? profile?.id,
    [authUserSession, profile?.id]
  );

  const analyticsEmail = useMemo(() => {
    const sessionUserIdRaw = getAuthenticatedActorId(authUserSession);
    const sessionUserId =
      sessionUserIdRaw != null ? Number(sessionUserIdRaw) : Number.NaN;

    if (!Number.isFinite(sessionUserId)) {
      return null;
    }

    return resolveSessionUserEmail(
      authUserSession ?? null,
      profile,
      sessionUserId
    );
  }, [authUserSession, profile]);

  useEffect(() => {
    if (isPostHogReady) {
      return;
    }

    return onPostHogLoaded(() => {
      setIsPostHogReady(true);
    });
  }, [isPostHogReady]);

  useEffect(() => {
    if (sessionLoading || !isPostHogReady) {
      return;
    }

    syncPostHogIdentity(analyticsUserId, analyticsEmail);

    posthog.set_config({
      capture_pageleave: true,
      disable_session_recording: false,
    });
    posthog.startSessionRecording();

    const handleRouteChange = () => {
      posthog.capture('$pageview');
    };

    handleRouteChange();
    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [
    analyticsEmail,
    analyticsUserId,
    isPostHogReady,
    router.events,
    sessionLoading,
  ]);

  return null;
};
